import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { Reveal, RevealLines } from "@/components/Reveal";
import { SectionLabel } from "@/components/SectionHeading";
import { Stats } from "@/components/Stats";
import { MagneticButton } from "@/components/MagneticButton";
import { PartnersStrip } from "@/components/PartnersStrip";
import { NetworkBackground } from "@/components/NetworkBackground";
import { ManifestoScroll } from "@/components/home/ManifestoScroll";
import { ServicesReveal } from "@/components/home/ServicesReveal";
import { ExperienceParallax } from "@/components/home/ExperienceParallax";
import { BentoWhy } from "@/components/home/BentoWhy";
import { ApproachStack } from "@/components/home/ApproachStack";
import { VelocityMarquee } from "@/components/home/VelocityMarquee";
import { TestimonialsDark } from "@/components/home/TestimonialsDark";
import { CTAFinale } from "@/components/home/CTAFinale";
import { STATS } from "@/data/content";
import { useSEO } from "@/lib/useSEO";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden" data-testid="hero-section">
      <div className="relative z-10 mx-auto flex min-h-[100svh] w-full max-w-[1500px] flex-col justify-center px-6 pb-20 pt-32 md:px-12">
        {/* copy */}
        <motion.div style={{ y, opacity }} className="max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
            <SectionLabel>Tangensis Business Solutions</SectionLabel>
          </motion.div>

          <h1 className="mt-6 font-display font-extrabold leading-[1.02] tracking-tight text-brand-ink text-4xl sm:text-5xl md:text-6xl lg:text-[4rem]">
            <RevealLines lines={["Built for growth."]} start={0.3} immediate />
            <RevealLines lines={["Designed for impact."]} start={0.45} immediate lineClassName="gradient-text-animated" />
          </h1>

          <Reveal delay={0.55}>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-brand-ink/75">
              Tangensis transforms business challenges into strategic opportunities — through
              innovative, practical and results-driven solutions across strategy, technology and talent.
            </p>
          </Reveal>

          <Reveal delay={0.7}>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <MagneticButton as="div">
                <Link to="/services" data-testid="hero-explore-services"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full gradient-bg px-8 py-4 text-base font-bold text-white shadow-lg shadow-brand-primary/25 transition-shadow duration-300 hover:shadow-brand-primary/40 sm:w-auto">
                  EXPLORE SERVICES <ArrowUpRight className="h-5 w-5" />
                </Link>
              </MagneticButton>
              <Link to="/projects" data-testid="hero-view-projects"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-brand-ink/20 px-8 py-4 text-base font-bold text-brand-ink transition-colors duration-300 hover:border-brand-primary hover:text-brand-primary sm:w-auto">
                VIEW PROJECTS
              </Link>
            </div>
          </Reveal>
        </motion.div>
      </div>

      <motion.div style={{ opacity }} className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-brand-ink/60 md:flex">
        <span className="text-xs tracking-[0.3em]">SCROLL</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.6, repeat: Infinity }}><ArrowDown className="h-4 w-4" /></motion.span>
      </motion.div>
    </section>
  );
};

export default function Home() {
  useSEO({
    title: "Tangensis Business Solutions | Strategy, Digital & Technology Consulting",
    description: "Tangensis transforms business challenges into strategic opportunities with innovative, practical, results-driven solutions across strategy, digital transformation, technology and talent.",
    path: "/",
  });
  return (
    <>
      {/* Technical blue node/line network on pure white — nodes deepen as they near the screen */}
      <NetworkBackground />

      <div className="relative z-10">
        <Hero />
        <PartnersStrip />
        <ManifestoScroll />
        <ServicesReveal />
        <ExperienceParallax />
        <BentoWhy />
        <ApproachStack />
        <VelocityMarquee />
        <Stats stats={STATS} />
        <TestimonialsDark />
        <CTAFinale />
      </div>
    </>
  );
}
